/*
  Samashti student session — global theme runtime.

  There is no longer a separate school storefront. A Samashti student shops the
  ordinary 6Hats store, so this runtime's job is to answer one question on every
  page — "is a validated school session present?" — and to let the rest of the
  theme react to the answer.

  What it must never do, and does not:

    - Trust the browser. The token is opaque and is validated against the
      Sannitha Worker on every page load. A forged or stale value reveals
      nothing, because the theme holds no school data to reveal.
    - Store or expose roster values. No student ID, no guardian phone, no name
      is written to the DOM, to a data attribute, to localStorage or to a URL.
      The only thing in browser storage is the opaque session token, and it
      lives in sessionStorage so it does not survive the tab.
    - Fail open. Everything school-related is hidden by CSS before this script
      runs and is revealed only after the Worker confirms the session. If the
      Worker is unreachable, the student sees the normal store and nothing else.

  The token key is deliberately unchanged from the previous architecture, so a
  guardian already signed in when this ships stays signed in.
*/
(function () {
  "use strict";

  var TOKEN_KEY = "sannitha:token";
  var SESSION_CLASS = "samashti-session-active";
  var CHECKED_CLASS = "samashti-session-checked";
  var SESSION_EVENT = "sixhats:samashti-session";

  var root = document.documentElement;
  var config = readConfig();

  /* ------------------------------------------------------------- storage */

  function readToken() {
    try {
      var value = window.sessionStorage.getItem(TOKEN_KEY);
      // Shape-checked before it is ever sent, so a junk value costs no request.
      return /^[a-f0-9]{64}$/.test(value || "") ? value : null;
    } catch (error) {
      return null;
    }
  }

  function clearToken() {
    try {
      window.sessionStorage.removeItem(TOKEN_KEY);
      // The old architecture's school cart key, cleared so a stale private cart
      // cannot linger now that everything uses the normal Shopify cart.
      window.sessionStorage.removeItem("sannitha:cart");
    } catch (error) {
      /* Nothing to clean up. */
    }
  }

  /* -------------------------------------------------------------- config */

  function readConfig() {
    var node = document.getElementById("samashti-session-config");
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || "{}") || {};
    } catch (error) {
      return {};
    }
  }

  function endpoint() {
    var value = String(config.endpoint || "").trim();
    if (!value) return "";
    return value.replace(/\/+$/, "");
  }

  /* ----------------------------------------------------------------- api */

  function api(path, options) {
    var base = endpoint();
    if (!base) return Promise.reject(new Error("not-configured"));

    var settings = options || {};
    var headers = { "content-type": "application/json" };
    var token = readToken();
    if (token) headers.authorization = "Bearer " + token;

    return fetch(base + path, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(settings.body || {}),
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (payload) {
          return { status: response.status, body: payload };
        });
    });
  }

  /* ------------------------------------------------------------- session */

  var state = { active: false, checked: false, pending: null };

  function announce() {
    root.classList.add(CHECKED_CLASS);
    root.classList.toggle(SESSION_CLASS, state.active);
    // Only a boolean crosses this boundary. Nothing about the student does.
    document.dispatchEvent(
      new CustomEvent(SESSION_EVENT, { detail: { active: state.active } })
    );
  }

  function settle(active) {
    state.active = active === true;
    state.checked = true;
    if (!state.active) clearToken();
    announce();
    return state.active;
  }

  /**
   * Validates the session once per page load and caches the promise, so ten
   * callers asking "is a student signed in?" cost one request.
   */
  function validate() {
    if (state.pending) return state.pending;

    /*
      A signed-in Shopify customer ends any student session on this device.

      Hiding the links would not be enough. The student session is a real
      entitlement — it opens the school collections and mints the 5% discount —
      so if it is not for the person now signed in, it has to be revoked rather
      than concealed. Calling sign-out also withdraws the unused discount
      server-side, which a CSS rule could never do.

      This is checked before the token is validated, so a school session cannot
      briefly come alive underneath someone else's account.
    */
    if (config.shopifyCustomerSignedIn === true) {
      state.pending = signOut().then(function () {
        return false;
      });
      return state.pending;
    }

    if (!readToken() || !endpoint()) {
      state.pending = Promise.resolve(settle(false));
      return state.pending;
    }

    state.pending = api("/auth/validate")
      .then(function (result) {
        return settle(result.status === 200 && result.body && result.body.valid === true);
      })
      .catch(function () {
        // Fail closed: an unreachable Worker means no student session.
        return settle(false);
      });

    return state.pending;
  }

  /* ---------------------------------------------------------------- gate */

  /**
   * School collection and product pages carry no protected data in Liquid — the
   * products are ordinary published Shopify products — but they are not for
   * general browsing, so a visitor without a session is sent to sign in.
   *
   * The redirect is a UX gate, not the access control. What actually protects
   * the school's roster is the Worker session; what keeps these products out of
   * search and recommendations is seo.hidden and the tag exclusions.
   */
  function applyGate() {
    if (!config.protectedRoute) return;

    validate().then(function (active) {
      if (active) return;
      var loginUrl = config.loginUrl || "/pages/sannitha";
      var target = window.location.pathname + window.location.search;
      window.location.replace(
        loginUrl + "?return=" + encodeURIComponent(target)
      );
    });
  }

  /* ------------------------------------------------------------ sign out */

  function signOut() {
    var token = readToken();
    var finish = function () {
      clearToken();
      settle(false);
      return true;
    };

    if (!token || !endpoint()) return Promise.resolve(finish());

    // The Worker also revokes the student's unused toy-benefit discount here, so
    // signing out withdraws the entitlement rather than leaving it live.
    return api("/auth/logout").then(finish, finish);
  }

  /* ------------------------------------------------- cart reconciliation */

  /**
   * Attaches the opaque cart reference so a paid order can be recognised as a
   * student purchase afterwards.
   *
   * The value is issued by the Worker and means nothing on its own — it is not
   * the student ID, not the session token, and not derived from either. A
   * shopper reading their own cart attributes learns nothing, and a fabricated
   * value resolves to no session and tags nobody.
   */
  function attachCartReference() {
    if (!state.active) return Promise.resolve(false);
    if (config.cartItemCount === 0) return Promise.resolve(false);
    if (config.cartHasReference === true) return Promise.resolve(true);

    return api("/cart/reference")
      .then(function (result) {
        if (result.status !== 200 || !result.body || !result.body.reference) return false;
        var attributes = {};
        attributes[result.body.attribute] = result.body.reference;
        return fetch(window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + "cart/update.js"
          : "/cart/update.js", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ attributes: attributes }),
        })
          .then(function () {
            config.cartHasReference = true;
            return true;
          })
          .catch(function () {
            return false;
          });
      })
      .catch(function () {
        return false;
      });
  }

  /* -------------------------------------------------------------- checkout */

  var checkoutInFlight = false;

  /**
   * Intercepts Checkout for an authenticated student so the 5% toy benefit is
   * applied without the student typing anything.
   *
   * The Worker returns a `/discount/<code>?redirect=/checkout` path — Shopify's
   * own shareable-discount mechanism — so the platform applies the code to the
   * existing cart and then forwards to checkout. The cart is never rebuilt.
   *
   * Every failure path falls through to the ordinary checkout. A benefit that
   * cannot be minted must never cost the sale.
   */
  function interceptCheckout(event) {
    if (checkoutInFlight) return;

    var button = event.target && event.target.closest
      ? event.target.closest('[name="checkout"], .cart__checkout-button')
      : null;
    if (!button || button.disabled) return;

    // A cart holding school items with no valid session must not reach
    // checkout: the student has to sign back in or remove those items.
    if (config.cartHasSchoolItems && !state.active) {
      event.preventDefault();
      event.stopPropagation();
      showSchoolItemNotice();
      return;
    }

    if (!state.active) return;

    event.preventDefault();
    checkoutInFlight = true;
    button.setAttribute("aria-busy", "true");

    var proceed = function () {
      checkoutInFlight = false;
      button.removeAttribute("aria-busy");
      // Submitting the form rather than clicking again avoids re-entering this
      // handler, and keeps whatever the theme's own cart form would have done.
      var form = button.form || document.getElementById("cart");
      if (form) form.submit();
      else window.location.assign("/checkout");
    };

    attachCartReference()
      .then(function () {
        return api("/benefit/prepare", { body: { redirect: "/checkout" } });
      })
      .then(function (result) {
        if (result.status === 200 && result.body && result.body.applyPath) {
          checkoutInFlight = false;
          button.removeAttribute("aria-busy");
          // Same-origin path only; the Worker refuses anything else.
          window.location.assign(result.body.applyPath);
          return;
        }
        proceed();
      })
      .catch(proceed);
  }

  function showSchoolItemNotice() {
    var notice = document.querySelector("[data-samashti-cart-notice]");
    if (notice) {
      notice.hidden = false;
      notice.setAttribute("role", "alert");
      notice.scrollIntoView({ block: "nearest", behavior: "smooth" });
      return;
    }
    // No placeholder on this template — say it plainly rather than silently
    // refusing to check out.
    window.alert(
      config.schoolItemMessage ||
        "Please sign in again as a Samashti student to continue with school items."
    );
  }

  /* ------------------------------------------------------------- wiring */

  function wireCheckoutButtons() {
    // Delegated at the document, because the cart drawer replaces its own
    // markup on every quantity change and a bound listener would be lost.
    document.addEventListener("click", interceptCheckout, true);
  }

  function wireSignOut() {
    document.addEventListener("click", function (event) {
      var trigger = event.target && event.target.closest
        ? event.target.closest("[data-samashti-signout]")
        : null;
      if (!trigger) return;
      event.preventDefault();
      trigger.setAttribute("aria-busy", "true");
      signOut().then(function () {
        trigger.removeAttribute("aria-busy");
        var after = trigger.getAttribute("data-samashti-signout-redirect");
        if (after) window.location.assign(after);
      });
    });
  }

  /* -------------------------------------------------------------- public */

  // A deliberately small surface: a boolean, a promise, and sign-out. Nothing
  // here can be used to read anything about the student.
  window.SixHatsSamashti = {
    isActive: function () {
      return state.active;
    },
    isChecked: function () {
      return state.checked;
    },
    ready: validate,
    signOut: signOut,
    onChange: function (listener) {
      document.addEventListener(SESSION_EVENT, function (event) {
        listener(event.detail && event.detail.active === true);
      });
      if (state.checked) listener(state.active);
    },
  };

  /* ----------------------------------------------------------------- init */

  wireCheckoutButtons();
  wireSignOut();

  validate().then(function (active) {
    if (active) attachCartReference();
  });

  applyGate();
})();
